import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/PageStyles.css';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: 'general',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Add animation class to elements when component mounts
    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el, index) => {
      setTimeout(() => {
        el.classList.add('animated');
      }, index * 100);
    });
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    else if (formData.message.length < 10) newErrors.message = 'Message must be at least 10 characters';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear error for this field
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      setSubmitted(true);
      setLoading(false);
      setFormData({
        name: '',
        email: '',
        subject: '',
        category: 'general',
        message: ''
      });
      
      // Reset after 5 seconds
      setTimeout(() => setSubmitted(false), 5000);
    }, 1500);
  };

  const contactInfo = [
    {
      icon: 'fas fa-map-marker-alt',
      title: 'Visit Us',
      content: ['Federal University Wukari', 'PMB 1020, Wukari', 'Taraba State, Nigeria'],
      color: 'primary'
    },
    {
      icon: 'fas fa-phone-alt',
      title: 'Call Us',
      content: ['+234 (0) 800 123 4567', '+234 (0) 800 765 4321', 'Mon-Fri: 8:00 AM - 5:00 PM'],
      color: 'success'
    },
    {
      icon: 'fas fa-envelope',
      title: 'Email Us',
      content: ['journal@fuwukari.edu.ng', 'editor@fuwjals.edu.ng', 'support@fuwjals.edu.ng'],
      color: 'info'
    }
  ];

  const socialLinks = [
    { icon: 'fab fa-facebook-f', url: '#', name: 'Facebook' },
    { icon: 'fab fa-twitter', url: '#', name: 'Twitter' },
    { icon: 'fab fa-linkedin-in', url: '#', name: 'LinkedIn' },
    { icon: 'fab fa-researchgate', url: '#', name: 'ResearchGate' },
    { icon: 'fab fa-orcid', url: '#', name: 'ORCID' }
  ];

  return (
    <>
      {/* Page Header */}
      <div className="page-header contact-header">
        <div className="page-header-content">
          <h1 className="page-title animate-on-scroll">Get in Touch</h1>
          <p className="page-subtitle animate-on-scroll">
            We're here to help with your inquiries about submissions, publications, and journal services
          </p>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="breadcrumb-nav">
        <div className="container">
          <nav className="breadcrumb">
            <span className="breadcrumb-item">
              <Link to="/">Home</Link>
            </span>
            <span className="breadcrumb-item">
              <i className="fas fa-chevron-right"></i>
            </span>
            <span className="breadcrumb-item active">Contact</span>
          </nav>
        </div>
      </div>

      <div className="container">
        {/* Contact Information Cards */}
        <div className="row mb-5">
          {contactInfo.map((info, index) => (
            <div key={index} className="col-md-4 mb-4">
              <div className="contact-info-card animate-on-scroll" style={{'--animation-delay': `${index * 0.1}s`}}>
                <div className={`contact-icon bg-${info.color}`}>
                  <i className={info.icon}></i>
                </div>
                <h3 className="contact-info-title">{info.title}</h3>
                {info.content.map((line, idx) => (
                  <p key={idx} className="contact-info-text">
                    {line}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="row">
          {/* Contact Form */}
          <div className="col-lg-8 mb-5">
            <div className="contact-form-card animate-on-scroll">
              <h2 className="form-title">
                <i className="fas fa-paper-plane me-2 text-primary"></i>
                Send Us a Message
              </h2>
              <p className="form-subtitle">
                Have a question or need assistance? Fill out the form below and we'll get back to you as soon as possible.
              </p>

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group-modern">
                      <label className="form-label-modern">
                        Full Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        className={`form-control-modern ${errors.name ? 'is-invalid' : ''}`}
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleChange}
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group-modern">
                      <label className="form-label-modern">
                        Email Address <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        className={`form-control-modern ${errors.email ? 'is-invalid' : ''}`}
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={handleChange}
                      />
                      {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group-modern">
                      <label className="form-label-modern">
                        Subject <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="subject"
                        className={`form-control-modern ${errors.subject ? 'is-invalid' : ''}`}
                        placeholder="How can we help?"
                        value={formData.subject}
                        onChange={handleChange}
                      />
                      {errors.subject && <div className="invalid-feedback">{errors.subject}</div>}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group-modern">
                      <label className="form-label-modern">Category</label>
                      <select
                        name="category"
                        className="form-control-modern"
                        value={formData.category}
                        onChange={handleChange}
                      >
                        <option value="general">General Inquiry</option>
                        <option value="submission">Manuscript Submission</option>
                        <option value="review">Peer Review</option>
                        <option value="publication">Publication Status</option>
                        <option value="technical">Technical Support</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="form-group-modern">
                  <label className="form-label-modern">
                    Message <span className="text-danger">*</span>
                  </label>
                  <textarea
                    name="message"
                    className={`form-control-modern ${errors.message ? 'is-invalid' : ''}`}
                    rows="6"
                    placeholder="Tell us more about your inquiry..."
                    value={formData.message}
                    onChange={handleChange}
                  ></textarea>
                  {errors.message && <div className="invalid-feedback">{errors.message}</div>}
                  <small className="form-text">Minimum 10 characters</small>
                </div>

                <div className="form-buttons">
                  <button 
                    type="submit" 
                    className="btn btn-primary btn-lg"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane me-2"></i>
                        Send Message
                      </>
                    )}
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-outline-secondary btn-lg"
                    onClick={() => setFormData({
                      name: '',
                      email: '',
                      subject: '',
                      category: 'general',
                      message: ''
                    })}
                  >
                    <i className="fas fa-redo me-2"></i>
                    Reset Form
                  </button>
                </div>

                {submitted && (
                  <div className="alert alert-success mt-4 animate-fade-in">
                    <i className="fas fa-check-circle me-2"></i>
                    Thank you for your message! We'll get back to you within 24-48 hours.
                  </div>
                )}
              </form>
            </div>
          </div>

          {/* Sidebar */}
          <div className="col-lg-4">
            {/* Office Hours */}
            <div className="info-card mb-4 animate-on-scroll">
              <div className="info-card-icon">
                <i className="fas fa-clock"></i>
              </div>
              <h3 className="info-card-title">Office Hours</h3>
              <div className="office-hours">
                <div className="hours-row">
                  <span className="day">Monday - Friday</span>
                  <span className="time">8:00 AM - 5:00 PM</span>
                </div>
                <div className="hours-row">
                  <span className="day">Saturday</span>
                  <span className="time">9:00 AM - 2:00 PM</span>
                </div>
                <div className="hours-row">
                  <span className="day">Sunday</span>
                  <span className="time text-danger">Closed</span>
                </div>
              </div>
              <p className="mt-3 text-secondary">
                <small>*Response times may vary during holidays and academic breaks</small>
              </p>
            </div>

            {/* Quick Response */}
            <div className="info-card mb-4 animate-on-scroll">
              <div className="info-card-icon">
                <i className="fas fa-bolt"></i>
              </div>
              <h3 className="info-card-title">Quick Response</h3>
              <p className="info-card-text">
                For urgent matters related to manuscript submission or publication, please email:
              </p>
              <a href="mailto:urgent@fuwjals.edu.ng" className="btn btn-sm btn-primary">
                <i className="fas fa-envelope me-2"></i>
                urgent@fuwjals.edu.ng
              </a>
            </div>

            {/* Social Media */}
            <div className="info-card animate-on-scroll">
              <h3 className="info-card-title">Connect With Us</h3>
              <p className="info-card-text">
                Follow us on social media for updates and announcements
              </p>
              <div className="social-links">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    className="social-link-btn"
                    title={social.name}
                    aria-label={social.name}
                  >
                    <i className={social.icon}></i>
                  </a>
                ))}
              </div>
            </div>

            {/* FAQ Link */}
            <div className="info-card mt-4 animate-on-scroll">
              <div className="text-center">
                <i className="fas fa-question-circle fa-3x text-primary mb-3"></i>
                <h3 className="info-card-title">Have Questions?</h3>
                <p className="info-card-text">
                  Check out our frequently asked questions for quick answers
                </p>
                <Link to="/faq" className="btn btn-outline-primary">
                  View FAQ
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="map-section animate-on-scroll">
          <h2 className="text-center mb-4">Find Us on Map</h2>
          <div className="map-container">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3951.7959379755!2d9.7831!3d7.9167!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zN8KwNTUnMDAuMSJOIDnCsDQ2JzU5LjIiRQ!5e0!3m2!1sen!2sng!4v1234567890"
              width="100%"
              height="400"
              style={{border: 0}}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="FUWJALS Location"
            ></iframe>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;